/**
 * In-memory page-state cache.
 *
 * Keeps loaded data + form filters around when the user navigates between
 * pages (e.g., Appointments → Receipts → Appointments). Lives for the life
 * of the React app — a full page refresh / new tab clears it (intentional,
 * so users explicitly start fresh when they reload the browser).
 *
 * Usage:
 *   import { getCached, setCached, clearCached } from "@/lib/pageStateCache";
 *
 *   // On mount — hydrate from cache if available
 *   const cached = getCached("appointments");
 *   if (cached) { setRows(cached.rows); setFilters(cached.filters); }
 *
 *   // After a fetch — save the snapshot
 *   setCached("appointments", { rows, filters, ts: Date.now() });
 */

const cache = new Map();

export function getCached(key) {
  return cache.has(key) ? cache.get(key) : null;
}

export function setCached(key, value) {
  cache.set(key, value);
}

export function clearCached(key) {
  cache.delete(key);
}

/** Optional helper — returns `true` if the cache entry is older than `maxAgeMs`. */
export function isStale(key, maxAgeMs) {
  const entry = cache.get(key);
  if (!entry || typeof entry.ts !== "number") return true;
  return Date.now() - entry.ts > maxAgeMs;
}
